import { resolveServiceBaseUrl } from "~/lib/resolveServiceBaseUrl";

import { env } from "./env";
import { resolveKeycloakRealm, resolveNotificationServiceBaseUrl } from "./runtimeConfig";

export const apiConfig = {
  recordServiceBaseUrl: resolveServiceBaseUrl(env.VITE_RECORD_SERVICE_BASE_URL),
  engineBaseUrl: resolveServiceBaseUrl(env.VITE_ENGINE_BASE_URL),
  documentServiceBaseUrl: resolveServiceBaseUrl(env.VITE_DOCUMENT_SERVICE_BASE_URL),
  schemaRegistryBaseUrl: resolveServiceBaseUrl(env.VITE_SCHEMA_REGISTRY_BASE_URL),
  workflowServiceBaseUrl: resolveServiceBaseUrl(env.VITE_WORKFLOW_SERVICE_BASE_URL),
  // Getter: the worker host is injected by `/runtime-config.js` after the module loads.
  get notificationServiceBaseUrl() {
    return resolveNotificationServiceBaseUrl();
  },
  // Centric Vertical Provisioner API — flavour provisioning (git → tenant).
  verticalProvisionerBaseUrl: resolveServiceBaseUrl(env.VITE_VERTICAL_PROVISIONER_BASE_URL),
  // Seeds the flavour-provisioning configure form; every request can override these.
  verticalProvisionerSource: {
    url: env.VITE_VERTICAL_PROVISIONER_SOURCE_URL,
    ref: env.VITE_VERTICAL_PROVISIONER_SOURCE_REF,
    root: env.VITE_VERTICAL_PROVISIONER_SOURCE_ROOT,
  },
  // centric-colour-api — ASE parsing, print colour analysis, point sampling.
  colourApiBaseUrl: resolveServiceBaseUrl(env.VITE_COLOUR_API_BASE_URL),
  // colour-api registers `cpes-dummy`, `cpes-record-service` and `centric-colour-api`
  // only — never `cpes-admin-portal`, so it cannot reuse the record-service identity
  // the way most services do. Same shape as the vertical provisioner below.
  colourApiApiKey: env.VITE_COLOUR_API_KEY || "DUMMY-123",
  colourApiServiceName: env.VITE_COLOUR_API_SERVICE_NAME || "cpes-record-service",
  expressionApiBaseUrl: resolveServiceBaseUrl(env.VITE_EXPRESSION_API_BASE_URL),
  expressionApiApiKey: env.VITE_EXPRESSION_API_KEY || "DUMMY-123",
  expressionApiServiceName: env.VITE_EXPRESSION_API_SERVICE_NAME || "cpes-record-service",
  documentBucketName: env.VITE_DOCUMENT_BUCKET_NAME,
  // Document-service shares the record-service identity (cpes-admin-portal + its
  // api key) unless the dedicated VITE_DOCUMENT_SERVICE_* vars are set for a
  // local compose that registers a separate identity.
  documentServiceApiKey: env.VITE_DOCUMENT_SERVICE_API_KEY || env.VITE_API_KEY,
  documentServiceName: env.VITE_DOCUMENT_SERVICE_NAME || env.VITE_SERVICE_NAME,
  // Prefer dedicated provisioner identity; else inherit the app identity (cloud
  // builds bake VITE_API_KEY / VITE_SERVICE_NAME — the provisioner registers
  // `cpes-admin-portal`); else local-compose defaults (`cpes-record-service` +
  // `DUMMY-123`). Override with VITE_VERTICAL_PROVISIONER_* for a local compose
  // that only registers the record-service client while VITE_API_KEY is set.
  verticalProvisionerApiKey:
    env.VITE_VERTICAL_PROVISIONER_API_KEY || env.VITE_API_KEY || "DUMMY-123",
  verticalProvisionerServiceName:
    env.VITE_VERTICAL_PROVISIONER_SERVICE_NAME ||
    (env.VITE_VERTICAL_PROVISIONER_API_KEY || env.VITE_API_KEY
      ? env.VITE_SERVICE_NAME
      : "cpes-record-service"),
  apiKey: env.VITE_API_KEY,
  serviceName: env.VITE_SERVICE_NAME,
  releaseCreateItemConcurrency: env.VITE_RELEASE_CREATE_ITEM_CONCURRENCY,
  releaseCreateLinkRetries: env.VITE_RELEASE_CREATE_LINK_RETRIES,
  releaseCreatePreLinkDelayMs: env.VITE_RELEASE_CREATE_PRELINK_DELAY_MS,
  timeout: 120_000,
  /**
   * Flavour apply holds one HTTP connection until every artefact is written.
   * `0` disables the client abort timer so the UI does not declare a timeout
   * while the provisioner is still working. The server still bounds the call
   * (Knative `timeoutSeconds`).
   */
  provisioningTimeout: 0,

  /**
   * Keycloak Admin REST API base URL.
   * Requests are proxied through the reverse proxy (`/keycloak-admin` → Keycloak)
   * to avoid CORS issues in both dev and production.
   */
  get keycloakAdminBaseUrl() {
    return `/keycloak-admin/realms/${resolveKeycloakRealm()}`;
  },
};
